<template>
  <div class="sci-page-wrapper" style="font-family: 'Inter', sans-serif;">
    <div class="max-w-6xl mx-auto space-y-8">
      
      <!-- HEADER -->
      <div class="sci-page-header pb-4 flex justify-between items-center">
        <div>
          <h1 class="sci-page-title-only">Gestão de Cartões Black</h1>
          <p class="text-sm text-slate-500">Configure limites, senhas e acompanhe o consumo em tempo real</p>
        </div>
      </div>

      <!-- DASHBOARD DE MONITORAMENTO RÁPIDO -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div class="bg-slate-900 rounded-xl p-6 border border-white/10 shadow-xl overflow-hidden relative group">
           <div class="relative z-10">
             <span class="text-[10px] font-bold text-white/50 uppercase tracking-widest block mb-1">Total de Cartões</span>
             <h3 class="text-3xl font-black text-white">{{ cards.length }}</h3>
           </div>
            <v-icon class="absolute -right-12 top-1/2" style="color: rgba(255,255,255,0.08); font-size: 60px; transform: translateY(-50%) rotate(-15deg);">mdi-card-bulleted</v-icon>
        </div>
        <div class="bg-slate-900 rounded-xl p-6 border border-white/10 shadow-xl overflow-hidden relative group">
           <div class="relative z-10">
             <span class="text-[10px] font-bold text-white/50 uppercase tracking-widest block mb-1">Limite Total do Mês</span>
             <h3 class="text-2xl font-black text-white">R$ {{ totalLimit.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) }}</h3>
           </div>
            <v-icon class="absolute -right-12 top-1/2" style="color: rgba(255,255,255,0.08); font-size: 60px; transform: translateY(-50%) rotate(-15deg);">mdi-cash-multiple</v-icon>
        </div>
        <div class="bg-slate-900 rounded-xl p-6 border border-white/10 shadow-xl overflow-hidden relative group">
           <div class="relative z-10">
             <span class="text-[10px] font-bold text-white/50 uppercase tracking-widest block mb-1">Total Utilizado</span>
             <h3 class="text-2xl font-black text-orange-500">R$ {{ totalUsed.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) }}</h3>
           </div>
            <v-icon class="absolute -right-12 top-1/2" style="color: rgba(255,255,255,0.08); font-size: 60px; transform: translateY(-50%) rotate(-15deg);">mdi-trending-up</v-icon>
        </div>
        <div class="bg-slate-900 rounded-xl p-6 border border-white/10 shadow-xl overflow-hidden relative group">
           <div class="relative z-10">
             <span class="text-[10px] font-bold text-white/50 uppercase tracking-widest block mb-1">Saldo Disponível</span>
             <h3 class="text-2xl font-black text-emerald-400">R$ {{ (totalLimit - totalUsed).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) }}</h3>
           </div>
            <v-icon class="absolute -right-12 top-1/2" style="color: rgba(255,255,255,0.08); font-size: 60px; transform: translateY(-50%) rotate(-15deg);">mdi-check-decagram</v-icon>
        </div>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <!-- FORMULÁRIO (COL 1) -->
        <div class="lg:col-span-1">
          <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm sticky top-8">
            <div class="px-6 py-4 flex items-center gap-3" style="background-color: #333333;">
                <v-icon color="orange" small>mdi-credit-card-plus</v-icon>
                <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">
                    {{ cardData.card_id ? 'EDITAR CARTÃO' : 'EMITIR NOVO CARTÃO' }}
                </h2>
            </div>
            
            <div class="p-6">
                <v-form @submit.prevent="createOrUpdate">
                  <div class="space-y-4">
                    <div v-if="$store.getters.getUserData.group_id === 1">
                      <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Prefeitura</label>
                      <v-autocomplete
                        v-model="cardData.prefecture_id"
                        :items="prefectures"
                        item-text="prefecture_social_name"
                        item-value="prefecture_id"
                        placeholder="Selecione a prefeitura..."
                        outlined dense hide-details class="custom-input"
                        @change="onPrefectureChange"
                      />
                    </div>

                    <div>
                      <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Portador (Cliente)</label>
                      <v-autocomplete
                        v-model="cardData.client_id"
                        :items="availableClients"
                        item-text="client_name"
                        item-value="client_id"
                        placeholder="Escolha o portador..."
                        :disabled="!cardData.prefecture_id"
                        outlined dense hide-details class="custom-input"
                      />
                    </div>

                    <div>
                      <div class="flex justify-between items-center mb-1">
                        <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider">Número do Cartão (16 dígitos)</label>
                        <button type="button" @click="generateCardNumber" class="text-[9px] font-black text-orange-500 hover:text-orange-600 uppercase tracking-tighter">
                          [ GERAR NOVO ]
                        </button>
                      </div>
                      <v-text-field
                        v-model="cardData.card_number"
                        v-mask="'#### #### #### ####'"
                        placeholder="0000 0000 0000 0000"
                        outlined dense hide-details class="custom-input"
                      />
                    </div>

                    <div class="grid grid-cols-2 gap-4">
                      <div>
                        <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Senha (PIN)</label>
                        <v-text-field
                          v-model="cardData.card_password"
                          type="password"
                          placeholder="Ex: 1234"
                          outlined dense hide-details class="custom-input"
                        />
                      </div>
                      <div>
                        <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Dia de Reinício</label>
                        <v-select
                          v-model="cardData.reset_day"
                          :items="[1,2,3,4,5,10,15,20,25]"
                          outlined dense hide-details class="custom-input"
                        />
                      </div>
                    </div>

                     <div class="mb-4">
                       <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Tipo de Limite</label>
                       <v-radio-group v-model="cardData.card_limit_type" row hide-details class="mt-0 pt-0">
                         <v-radio label="Por Valor (R$)" value="VALUE" color="orange" class="mr-4"></v-radio>
                         <v-radio label="Por Litros (L)" value="LITERS" color="orange"></v-radio>
                       </v-radio-group>
                     </div>

                     <div v-if="cardData.card_limit_type === 'LITERS'">
                       <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Limite Mensal (Litros)</label>
                       <v-text-field
                         v-model="cardData.card_limit_liters"
                         placeholder="0,000"
                         suffix="L"
                         outlined dense hide-details class="custom-input"
                       />
                     </div>
                     <div v-else>
                       <label class="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">Limite Mensal (R$)</label>
                       <v-text-field
                         v-model="cardData.card_limit"
                         placeholder="0,00"
                         prefix="R$"
                         outlined dense hide-details class="custom-input"
                       />
                     </div>
                  </div>

                  <div class="mt-8 pt-6 border-t border-slate-100 space-y-2">
                    <button 
                      type="submit"
                      class="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold text-[11px] py-3 rounded shadow-lg transition-all uppercase flex items-center justify-center gap-2"
                    >
                      <v-icon small color="white">mdi-check</v-icon>
                      {{ cardData.card_id ? 'ATUALIZAR CARTÃO' : 'ATIVAR CARTÃO' }}
                    </button>
                    <button 
                      v-if="cardData.card_id"
                      type="button"
                      @click="newData"
                      class="w-full bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold text-[11px] py-3 rounded transition-all uppercase"
                    >
                      CANCELAR
                    </button>
                  </div>
                </v-form>
            </div>
          </section>
        </div>

        <!-- LISTAGEM E MONITORAMENTO (COL 2-3) -->
        <div class="lg:col-span-2">
          <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
              <div class="px-6 py-4 flex items-center justify-between" style="background-color: #313131;">
                  <div class="flex items-center gap-3">
                    <v-icon color="orange" small>mdi-monitor-dashboard</v-icon>
                    <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">ACOMPANHAMENTO DE LIMITES</h2>
                  </div>
                  <div class="relative w-64">
                    <input 
                      v-model="search"
                      placeholder="PESQUISAR..."
                      class="bg-white/10 border border-white/20 rounded-lg py-1.5 pl-4 pr-4 text-[10px] text-white placeholder:text-white/40 focus:outline-none focus:bg-white/20 transition-all w-full uppercase tracking-widest"
                    >
                  </div>
              </div>

              <div class="p-0">
                  <v-data-table
                    :headers="headers"
                    :items="cards"
                    :search="search"
                    :loading="loader"
                    class="custom-table"
                  >
                    <template v-slot:item.card_number="{ item }">
                      <div class="flex items-center gap-3 py-2">
                        <div class="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                          <v-icon color="slate-400">mdi-credit-card-chip-outline</v-icon>
                        </div>
                        <div class="flex flex-col">
                          <span class="text-xs font-black text-slate-700 tracking-wider">{{ item.card_number }}</span>
                          <span class="text-[10px] text-slate-400 uppercase font-bold">{{ item.client_name }}</span>
                        </div>
                      </div>
                    </template>

                    <template v-slot:item.usage="{ item }">
                      <div class="w-full py-4 min-w-[200px]">
                        <div class="flex justify-between items-end mb-1">
                          <span class="text-[10px] font-bold text-slate-400 uppercase">CONSUMO MENSAL</span>
                          <span class="text-[11px] font-black" :class="getUsageColor(item)">{{ calculatePercent(item) }}%</span>
                        </div>
                        <v-progress-linear
                          :value="calculatePercent(item)"
                          height="8"
                          rounded
                          :color="getUsageProgressColor(item)"
                          background-color="slate-100"
                        ></v-progress-linear>
                         <div class="flex justify-between mt-1 text-[10px] font-bold text-slate-500">
                           <template v-if="item.card_limit_type === 'LITERS'">
                             <span>{{ Number(item.card_used_liters || 0).toFixed(3) }} L</span>
                             <span>LIMITE: {{ Number(item.card_limit_liters || 0).toFixed(3) }} L</span>
                           </template>
                           <template v-else>
                             <span>R$ {{ Number(item.card_used_limit || 0).toFixed(2) }}</span>
                             <span>LIMITE: R$ {{ Number(item.card_limit || 0).toFixed(2) }}</span>
                           </template>
                         </div>
                      </div>
                    </template>

                    <template v-slot:item.reset="{ item }">
                       <div class="flex flex-col items-center">
                         <span class="text-xs font-bold text-slate-600">DIA {{ item.reset_day }}</span>
                         <span class="text-[9px] text-slate-400 uppercase tracking-tighter">REINÍCIO</span>
                       </div>
                    </template>

                    <template v-slot:item.action="{ item }">
                      <div class="flex justify-end gap-1 px-2">
                        <button @click="openCardPreview(item)" class="p-2 hover:bg-slate-100 text-slate-500 rounded-lg transition-colors" title="Gerar Cartão PNG">
                          <v-icon small>mdi-credit-card-outline</v-icon>
                        </button>
                        <button @click="loadData(item)" class="p-2 hover:bg-slate-100 text-slate-500 rounded-lg transition-colors">
                          <v-icon small>mdi-cog-outline</v-icon>
                        </button>
                        <button @click="deleteItem(item.card_id)" class="p-2 hover:bg-red-50 text-red-400 rounded-lg transition-colors">
                          <v-icon small>mdi-delete-outline</v-icon>
                        </button>
                      </div>
                    </template>
                  </v-data-table>
              </div>
          </section>
        </div>
      </div>
    </div>

    <!-- Black Card Premium Preview & Generate PNG -->
    <v-dialog v-model="cardPreviewDialog" max-width="480" overlay-opacity="0.8" overlay-color="#000">
      <v-card class="rounded-xl overflow-hidden" style="background-color: #121214; color: #fff;">
        <div class="px-6 py-4 flex items-center justify-between border-b border-white/10" style="background-color: #1a1a1e;">
          <div class="flex items-center gap-3">
            <v-icon color="orange" small>mdi-credit-card-outline</v-icon>
            <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">VISUALIZAR CARTÃO BLACK</h2>
          </div>
          <v-btn icon dark small @click="cardPreviewDialog = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="p-6 flex flex-col items-center justify-center">
          <!-- Premium Credit Card Wrapper -->
          <div 
            ref="blackCardElement"
            style="width: 400px; height: 250px; background: linear-gradient(135deg, #15161a 0%, #050608 100%); border: 1px solid rgba(212,175,55,0.35); border-radius: 16px; padding: 20px; box-shadow: 0 20px 45px rgba(0,0,0,0.6); position: relative; overflow: hidden; display: flex; flex-direction: column; justify-content: space-between; box-sizing: border-box;"
          >
            <!-- Background subtle premium gold/dark lines/glow -->
            <div style="position: absolute; inset: 0; pointer-events: none; opacity: 0.25; background: radial-gradient(circle at 85% 15%, rgba(212,175,55,0.25) 0%, transparent 60%); z-index: 1;"></div>
            <div style="position: absolute; left: -60px; top: -60px; width: 120px; height: 120px; border-radius: 50%; filter: blur(40px); opacity: 0.08; background-color: #d4af37; z-index: 1;"></div>
            
            <!-- Top Row: Logo & Card Type -->
            <div style="display: flex; justify-content: space-between; align-items: flex-start; z-index: 10; font-family: 'Inter', sans-serif;">
              <div style="display: flex; align-items: center; gap: 8px;">
                <!-- System logo -->
                <img src="@/assets/logosimbolo.png" alt="Sigfrota" style="height: 28px; width: auto; filter: drop-shadow(0 2px 4px rgba(0,0,0,0.5)); display: block;" />
                <span style="font-size: 11px; font-weight: 900; color: #ffffff; letter-spacing: 2px; line-height: 1;">SIGFROTA</span>
              </div>
              <div style="display: flex; flex-direction: column; align-items: flex-end; line-height: 1;">
                <span style="font-size: 13px; font-weight: 800; color: #d4af37; letter-spacing: 3px;">BLACK</span>
                <span style="font-size: 7px; color: rgba(255,255,255,0.5); letter-spacing: 1px; text-transform: uppercase; margin-top: 2px;">PREMIUM</span>
              </div>
            </div>

            <!-- Middle Row: Chip, Contactless Indicator -->
            <div style="display: flex; align-items: center; justify-content: space-between; z-index: 10; margin-top: 8px;">
              <!-- Metallic Chip -->
              <div 
                style="width: 44px; height: 32px; border-radius: 6px; position: relative; overflow: hidden; border: 1px solid rgba(180, 130, 40, 0.4); flex-shrink: 0; box-shadow: inset 0 1px 2px rgba(255,255,255,0.3), 0 1px 2px rgba(0,0,0,0.3); background: linear-gradient(135deg, #fce0ad 0%, #dfac52 50%, #c48e30 100%);"
              >
                <!-- Chip grid lines -->
                <div style="position: absolute; inset: 0; display: grid; grid-template-columns: repeat(3, 1fr); grid-template-rows: repeat(3, 1fr); opacity: 0.25; pointer-events: none;">
                  <div style="border-right: 1px solid #000; border-bottom: 1px solid #000;"></div>
                  <div style="border-right: 1px solid #000; border-bottom: 1px solid #000;"></div>
                  <div style="border-bottom: 1px solid #000;"></div>
                  <div style="border-right: 1px solid #000; border-bottom: 1px solid #000;"></div>
                  <div style="border-right: 1px solid #000; border-bottom: 1px solid #000;"></div>
                  <div style="border-bottom: 1px solid #000;"></div>
                  <div style="border-right: 1px solid #000;"></div>
                  <div style="border-right: 1px solid #000;"></div>
                  <div></div>
                </div>
                <!-- Shiny center -->
                <div style="width: 10px; height: 10px; border-radius: 50%; background: rgba(255,255,255,0.25); position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%); filter: blur(1px);"></div>
              </div>
              <!-- Contactless Icon -->
              <v-icon dark style="transform: rotate(90deg); opacity: 0.8; font-size: 20px; color: #d4af37;">mdi-wifi</v-icon>
            </div>

            <!-- Bottom Row: Card Details -->
            <div style="display: flex; flex-direction: column; justify-content: flex-end; z-index: 10; margin-top: 8px;">
              <!-- Card Number -->
              <div style="font-size: 20px; font-weight: bold; letter-spacing: 4px; text-align: center; margin-bottom: 10px; color: #f3e5ab; text-shadow: 1px 1px 2px rgba(0,0,0,0.8); font-family: 'Courier New', Courier, monospace; width: 100%;">
                {{ formatCardNumber(selectedCard.card_number) }}
              </div>
              
              <!-- Portador and Expiry/Cycle -->
              <div style="display: flex; justify-content: space-between; align-items: flex-end; font-family: 'Inter', sans-serif; color: rgba(255,255,255,0.75); width: 100%;">
                <div style="display: flex; flex-direction: column; max-width: 270px; line-height: 1.4;">
                  <div style="font-size: 7px; color: rgba(255,255,255,0.45); text-transform: uppercase; letter-spacing: 1px; margin-bottom: 2px;">Portador</div>
                  <div style="font-size: 11px; font-weight: bold; color: #ffffff; text-shadow: 0 1px 2px rgba(0,0,0,0.5); letter-spacing: 1px; text-transform: uppercase;">{{ selectedCard.client_name }}</div>
                  <div style="font-size: 7px; color: rgba(255,255,255,0.45); text-transform: uppercase; letter-spacing: 0.5px; margin-top: 3px;">{{ selectedCard.prefecture_social_name }}</div>
                </div>
                <div style="display: flex; flex-direction: column; align-items: flex-end; flex-shrink: 0; line-height: 1.4;">
                  <div style="font-size: 7px; color: rgba(255,255,255,0.45); text-transform: uppercase; letter-spacing: 1px; margin-bottom: 2px;">Reinício</div>
                  <div style="font-size: 12px; font-weight: bold; color: #d4af37;">DIA {{ String(selectedCard.reset_day || 1).padStart(2, '0') }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="bg-white/5 px-6 py-4 flex justify-between items-center border-t border-white/10">
          <v-btn text dark small @click="cardPreviewDialog = false">FECHAR</v-btn>
          <v-btn 
            color="orange" 
            class="white--text text-[11px] font-bold py-2.5 px-6 rounded uppercase" 
            elevation="0" 
            :loading="exportLoader"
            @click="exportCardAsPng"
          >
            <v-icon small class="mr-2">mdi-download</v-icon>
            Baixar PNG
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Feedback -->
    <v-snackbar v-model="snack.show" :color="snack.color" timeout="3000" top right>
      <div class="flex items-center gap-3">
        <v-icon color="white" small>{{ snack.color === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
        <span class="text-[11px] font-bold uppercase tracking-wider">{{ snack.message }}</span>
      </div>
    </v-snackbar>
  </div>
</template>

<script>
import html2canvas from "html2canvas";

export default {
  data: () => ({
    cardPreviewDialog: false,
    exportLoader: false,
    selectedCard: {},
    search: "",
    loader: false,
    cards: [],
    clients: [],
    cardData: {
      card_id: null,
      card_number: "",
      card_password: "",
      card_limit: 0,
      reset_day: 1,
      client_id: null,
      prefecture_id: null
    },
    prefectures: [],
    snack: {
      show: false,
      message: "",
      color: "success"
    },
    headers: [
      { text: "Cartão / Portador", align: "start", value: "card_number", sortable: true },
      { text: "Monitoramento de Uso", align: "start", value: "usage", sortable: true, width: "300px" },
      { text: "Ciclo", align: "center", value: "reset", sortable: true },
      { text: "", align: "right", value: "action", sortable: false }
    ]
  }),
  computed: {
    totalLimit() {
      return this.cards.reduce((acc, c) => acc + Number(c.card_limit), 0);
    },
    totalUsed() {
      return this.cards.reduce((acc, c) => acc + Number(c.card_used_limit), 0);
    },
    availableClients() {
      if (!this.cardData.prefecture_id) return [];
      
      // Filtrar clientes pela prefeitura selecionada
      let list = this.clients.filter(c => c.prefecture_id === this.cardData.prefecture_id);
      
      // Se estiver cadastrando um novo cartão, remover clientes que já possuem um card ID vinculado
      // Se estiver editando, permitir o cliente atual do cartão
      const linkedClientIds = this.cards.map(c => c.client_id);
      
      return list.filter(c => {
        // Se o cliente NÃO tem cartão vinculado, ele está disponível
        if (!linkedClientIds.includes(c.client_id)) return true;
        
        // Se estamos EDITANDO e este é o cliente do cartão atual, ele deve aparecer
        if (this.cardData.card_id && c.client_id === this.cardData.client_id) return true;
        
        return false;
      });
    }
  },
  created() {
    this.newData();
    this.fetchClients();
    this.fetchCards();
    this.fetchPrefectures();
  },
  methods: {
    fetchClients() {
      this.$http.get("/settings/black-cards/clients").then(r => {
        this.clients = r.data;
      });
    },
    fetchPrefectures() {
      this.$http.get("/prefectures").then(r => {
        this.prefectures = r.data;
      });
    },
    fetchCards() {
      this.loader = true;
      this.$http.get("/settings/black-cards").then(r => {
        this.cards = r.data;
        this.loader = false;
      }).catch(() => { this.loader = false; });
    },
    calculatePercent(item) {
      if (item.card_limit_type === 'LITERS') {
        if (!item.card_limit_liters || item.card_limit_liters == 0) return 0;
        const pc = (Number(item.card_used_liters || 0) / Number(item.card_limit_liters)) * 100;
        return Math.min(pc, 100).toFixed(1);
      } else {
        if (!item.card_limit || item.card_limit == 0) return 0;
        const pc = (Number(item.card_used_limit || 0) / Number(item.card_limit)) * 100;
        return Math.min(pc, 100).toFixed(1);
      }
    },
    getUsageColor(item) {
      const pc = this.calculatePercent(item);
      if (pc > 90) return 'text-red-600';
      if (pc > 70) return 'text-orange-500';
      return 'text-slate-700';
    },
    getUsageProgressColor(item) {
      const pc = this.calculatePercent(item);
      if (pc > 90) return '#ef4444';
      if (pc > 70) return '#f97316';
      return '#3b82f6';
    },
    onPrefectureChange() {
      // Quando trocar a prefeitura, limpa o portador selecionado
      this.cardData.client_id = null;
    },
    newData() {
      const user = this.$store.getters.getUserData;
      this.cardData = {
        card_id: null,
        card_number: "",
        card_password: "",
        card_limit: 0,
        card_limit_liters: 0,
        card_used_limit: 0,
        card_used_liters: 0,
        card_limit_type: "VALUE",
        reset_day: 1,
        client_id: null,
        prefecture_id: user.group_id === 1 ? null : (user.prefecture_id || user.table_id)
      };
      this.generateCardNumber();
    },
    generateCardNumber() {
      let num = "";
      for (let i = 0; i < 16; i++) {
        num += Math.floor(Math.random() * 10).toString();
      }
      this.cardData.card_number = num;
    },
    loadData(item) {
      this.cardData = { 
        ...item,
        card_limit_type: item.card_limit_type || 'VALUE',
        card_limit_liters: item.card_limit_liters || 0,
        card_limit: item.card_limit || 0
      };
    },
    createOrUpdate() {
      const user = this.$store.getters.getUserData;
      if (user.group_id !== 1) {
        this.cardData.prefecture_id = user.prefecture_id || user.table_id;
      }
      
      // Clean up limits based on selection
      if (this.cardData.card_limit_type === 'LITERS') {
        this.cardData.card_limit = 0;
      } else {
        this.cardData.card_limit_liters = 0;
      }

      this.$store.dispatch("setLoader", true);
      const data = { ...this.cardData };
      data.card_number = data.card_number.replace(/\s/g, ''); // Remove spaces

      const request = data.card_id 
        ? this.$http.put(`/settings/black-cards/${data.card_id}`, data)
        : this.$http.post("/settings/black-cards", data);

      request.then(r => {
        this.snack.show = true;
        this.snack.message = r.data.result;
        this.snack.color = "success";
        this.fetchCards();
        this.newData();
        this.$store.dispatch("setLoader", false);
      }).catch(e => {
        this.snack.show = true;
        this.snack.message = e.message;
        this.snack.color = "error";
        this.$store.dispatch("setLoader", false);
      });
    },
    deleteItem(id) {
      if (confirm("Deseja cancelar e excluir este cartão permanentemente?")) {
        this.$http.delete(`/settings/black-cards/${id}`).then(r => {
          this.snack.show = true;
          this.snack.message = r.data.result;
          this.snack.color = "success";
          this.fetchCards();
        });
      }
    },
    openCardPreview(item) {
      this.selectedCard = item;
      this.cardPreviewDialog = true;
    },
    formatCardNumber(num) {
      if (!num) return "";
      const clean = num.replace(/\s/g, "");
      const matches = clean.match(/\d{4}/g);
      if (matches) {
        return matches.join(" ");
      }
      return num;
    },
    exportCardAsPng() {
      this.exportLoader = true;
      this.$nextTick(() => {
        const cardEl = this.$refs.blackCardElement;
        if (!cardEl) {
          this.exportLoader = false;
          return;
        }

        html2canvas(cardEl, {
          useCORS: true,
          backgroundColor: null,
          scale: 2, // higher resolution
          logging: false
        }).then(canvas => {
          const dataUrl = canvas.toDataURL("image/png");
          const link = document.createElement("a");
          const nameClean = (this.selectedCard.client_name || "cartao-black")
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "") // remove accents
            .replace(/[^a-z0-9]/g, "-")
            .replace(/-+/g, "-");
          link.download = `cartao-black-${nameClean}.png`;
          link.href = dataUrl;
          link.click();
          this.exportLoader = false;
          this.snack.show = true;
          this.snack.message = "CARTÃO PNG GERADO COM SUCESSO!";
          this.snack.color = "success";
        }).catch(e => {
          console.error("Erro ao gerar imagem do cartão", e);
          this.exportLoader = false;
          this.snack.show = true;
          this.snack.message = "ERRO AO GERAR PNG DO CARTÃO.";
          this.snack.color = "error";
        });
      });
    }
  }
};
</script>

<style scoped>
.custom-input >>> .v-input__slot {
  background-color: #f8fafc !important;
  border-radius: 8px !important;
}
.custom-input >>> fieldset {
  border-color: #e2e8f0 !important;
}
.custom-table >>> th {
  background-color: #f8fafc !important;
  font-size: 10px !important;
  font-weight: 700 !important;
  color: #64748b !important;
  text-transform: uppercase !important;
  letter-spacing: 0.05em !important;
  padding: 16px !important;
}
.custom-table >>> td {
  padding: 8px 16px !important;
  border-bottom: 1px solid #f1f5f9 !important;
}
</style>
